package com.trevorwiebe.timesheet.admin.reporting.domain

import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.TripType
import kotlinx.datetime.LocalDate

data class MissingStartEndBoundary(
    val truckId: String,
    val unitNumber: String,
    val endDate: LocalDate?,
    val startDate: LocalDate?,
    val missingEnd: Boolean,
    val missingStart: Boolean,
)

fun calculateMissingStartEndReport(
    entries: List<MileageEntry>,
    equipment: List<Equipment>,
): List<MissingStartEndBoundary> {
    return entries.groupBy { it.equipmentId }.flatMap { (truckId, truckEntries) ->
        val unitNumber = equipment.firstOrNull { it.id == truckId }?.unitNumber ?: truckId
        val days = truckEntries
            .groupBy { it.date.date }
            .mapValues { (_, dayEntries) ->
                dayEntries.sortedWith(compareBy({ it.date }, { it.mileageValue }))
            }
            .toList()
            .sortedBy { it.first }

        buildList {
            val (firstDay, firstDayEntries) = days.first()
            if (firstDayEntries.first().tripType != TripType.START_END) {
                add(
                    MissingStartEndBoundary(
                        truckId = truckId,
                        unitNumber = unitNumber,
                        endDate = null,
                        startDate = firstDay,
                        missingEnd = false,
                        missingStart = true,
                    )
                )
            }
            days.zipWithNext().forEach { (earlier, later) ->
                val missingEnd = earlier.second.last().tripType != TripType.START_END
                val missingStart = later.second.first().tripType != TripType.START_END
                if (missingEnd || missingStart) {
                    add(
                        MissingStartEndBoundary(
                            truckId = truckId,
                            unitNumber = unitNumber,
                            endDate = earlier.first,
                            startDate = later.first,
                            missingEnd = missingEnd,
                            missingStart = missingStart,
                        )
                    )
                }
            }
            val (lastDay, lastDayEntries) = days.last()
            if (lastDayEntries.last().tripType != TripType.START_END) {
                add(
                    MissingStartEndBoundary(
                        truckId = truckId,
                        unitNumber = unitNumber,
                        endDate = lastDay,
                        startDate = null,
                        missingEnd = true,
                        missingStart = false,
                    )
                )
            }
        }
    }.sortedWith(
        compareBy(
            { it.unitNumber },
            { it.endDate ?: it.startDate },
            { if (it.endDate == null) 0 else 1 },
        )
    )
}
